// ==========================================
// AdminSignupModel.js
// ==========================================
export class AdminSignupModel {
  /* ─────────────────────────────────────────────────────────────────────
     ALLOWED KEYS — exactly the fields buildAdminRequestPayload() writes
     plus the audit field `createdAt` used by submitAdminSignupRequest().
  ───────────────────────────────────────────────────────────────────── */
  static ALLOWED_KEYS = Object.freeze([
    // Identity
    "name",
    "email",
    "company",
    "companyId",
    "city",
    "location",       
    "stateCode",
    "countryCode",
    "requestedRole",
    "status",
    "submittedAt",
     "contactPersonMobile",
    "metadata",
  ]);

  static ALLOWED_METADATA_KEYS = Object.freeze([
    "createdAt",
    "createdBy",
    "updatedAt",
    "updatedBy",
    "deletedFlag",
  ]);

  static ID_FIELD   = "email";
  static NAME_FIELD = "name";

  static VALID_ROLES    = Object.freeze(["admin"]);
  static VALID_STATUSES = Object.freeze(["pending"]);

  /** Loose email check (mirrors HTML5 input[type=email]). */
  static EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  /** Company code: alphanumeric + optional dashes / underscores. */
  static COMPANY_CODE_REGEX = /^[A-Z0-9_-]+$/;

  /** Mobile: exactly 10 digits (matches form's maxLength + service validation). */
  static MOBILE_REGEX = /^\d{10}$/;

  constructor(data = {}, { isCreation = false } = {}) {
    /* ── 1. Seed defaults ─────────────────────────────────────────────── */
    for (const key of AdminSignupModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        this.metadata = {
          createdAt: null,
          createdBy: null,
          updatedAt: null,
          updatedBy: null,
          deletedFlag: false,
        };
      } else if (key === "status") {
        this.status = "pending";
      } else if (key === "requestedRole") {
        this.requestedRole = "admin";
      } else {
        this[key] = null;
      }
    }

    /* ── 2. Populate ──────────────────────────────────────────────────── */
    if (isCreation) {
      const now = new Date().toISOString();

      this.name                = data.name?.trim()                || null;
      this.email               = data.email?.trim().toLowerCase() || null;

      this.company             = data.company?.trim()             || null;
      this.companyId           = data.companyId?.trim().toUpperCase() || null;

      this.city                = data.city?.trim()                || null;
      this.location            = data.location?.trim() || data.city?.trim() || null;
      this.stateCode           = data.stateCode                   || null;
      this.countryCode         = data.countryCode                 || null;

      this.requestedRole       = data.requestedRole               || "admin";
      this.status              = data.status                      || "pending";
      this.submittedAt         = data.submittedAt                 || now;

      this.contactPersonMobile = data.contactPersonMobile?.trim() || null;

      this.createdAt           = data.createdAt                   || now;

      this.metadata = {
        createdAt: data.metadata?.createdAt || this.createdAt,
        createdBy: data.metadata?.createdBy || data.email || "system",
        updatedAt: data.metadata?.updatedAt || this.createdAt,
        updatedBy: data.metadata?.updatedBy || data.email || "system",
        deletedFlag: data.metadata?.deletedFlag ?? false,
      };
    } else {
      /* ── Update path — copy only whitelisted keys ─────────────────── */
      for (const key of Object.keys(data)) {
        if (!AdminSignupModel.ALLOWED_KEYS.includes(key)) continue;

        if (key === "metadata") {
          this.metadata = { ...this.metadata, ...(data.metadata || {}) };
        } else {
          this[key] = data[key];
        }
      }

      // Legacy top-level audit → metadata bridge
      if (data.createdAt) this.metadata.createdAt = data.createdAt;
      if (data.createdBy) this.metadata.createdBy = data.createdBy;
      if (data.updatedAt) this.metadata.updatedAt = data.updatedAt;
      if (data.updatedBy) this.metadata.updatedBy = data.updatedBy;
    }

    /* ── 3. Alias symmetry ─────────────────────────────────────────────── */
    if (this.city && !this.location)     this.location = this.city;
    if (this.location && !this.city)     this.city     = this.location;
  }

  /* ─────────────────────────────────────────────────────────────────────
     VALIDATION — mirrors validateAdminSignupForm() 1:1
     (email-existence check stays in the service — it's a DB call, not
      a model concern.)
  ───────────────────────────────────────────────────────────────────── */
  isValid() {
    // Required fields
    if (!this.name     || !String(this.name).trim())     return false;
    if (!this.email    || !String(this.email).trim())    return false;
    if (!this.company  || !String(this.company).trim())  return false;
    if (!this.companyId|| !String(this.companyId).trim())return false;
    if (!this.countryCode) return false;
    if (!this.stateCode)   return false;
    if (!this.city     || !String(this.city).trim())     return false;
    if (!this.contactPersonMobile) return false;

    // Email format
    if (!AdminSignupModel.EMAIL_REGEX.test(this.email)) return false;

    // Company ID format
    if (!AdminSignupModel.COMPANY_CODE_REGEX.test(this.companyId)) return false;

    // Mobile — exactly 10 digits
    const digits = String(this.contactPersonMobile).replace(/[\s\-()]/g, "");
    if (!AdminSignupModel.MOBILE_REGEX.test(digits)) return false;

    // Enum guards
    if (!AdminSignupModel.VALID_ROLES.includes(this.requestedRole)) return false;
    if (!AdminSignupModel.VALID_STATUSES.includes(this.status)) return false;

    return true;
  }

  /* ─────────────────────────────────────────────────────────────────────
     SERIALISATION
  ───────────────────────────────────────────────────────────────────── */
  toPayload() {
    const payload = {};
    for (const key of AdminSignupModel.ALLOWED_KEYS) {
      if (key === "metadata") {
        payload.metadata = {};
        for (const mk of AdminSignupModel.ALLOWED_METADATA_KEYS) {
          payload.metadata[mk] =
            this.metadata?.[mk] ?? (mk === "deletedFlag" ? false : null);
        }
      } else if (this[key] !== undefined) {
        payload[key] = this[key];
      }
    }
    return payload;
  }

  toJSON() {
    return this.toPayload();
  }
}